import type { TextareaHTMLAttributes } from "react";
import { forwardRef } from "react";

/**
 * Composant Textarea · même DA que Input.
 * Hauteur définie par la prop `rows` standard (défaut 3).
 */
type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className"> & {
  label?: string;
  hint?: string;
  error?: string;
  className?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, Props>(function Textarea(
  { label, hint, error, className = "", id, rows = 3, ...rest },
  ref,
) {
  const taId = id ?? rest.name;
  return (
    <div className={`w-full ${className}`}>
      {label ? (
        <label
          htmlFor={taId}
          className="block text-sm font-semibold text-navy-800"
        >
          {label}
          {rest.required ? (
            <span className="ml-1 text-accent-500" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <div
        className={`mt-1.5 rounded-xl bg-white px-3.5 py-2.5 ring-1 transition-all focus-within:ring-2 ${
          error
            ? "ring-red-300 focus-within:ring-red-500"
            : "ring-navy-200 focus-within:ring-accent-500"
        }`}
      >
        <textarea
          id={taId}
          ref={ref}
          rows={rows}
          className="w-full resize-y bg-transparent text-navy-800 placeholder:text-navy-400 focus:outline-none"
          aria-invalid={error ? "true" : undefined}
          aria-describedby={
            error ? `${taId}-err` : hint ? `${taId}-hint` : undefined
          }
          {...rest}
        />
      </div>
      {error ? (
        <p id={`${taId}-err`} className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${taId}-hint`} className="mt-1.5 text-xs text-navy-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
